import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { DateFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgSearchPurchaseOrderItem } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import {
  cellFactory,
  CurrencyCell,
  CuriosityLinkCell,
  DataGridConfig,
  DataGridLinkVariant,
  dateFormatterFactory,
  GridAlignmentType,
  gridColDefFactory,
  LinkCell,
  LinkCellValue,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { RouteId } from '~/routes/RouteId';

export enum PurchaseOrderItemSearchDataGridField {
  ID = 'id',
  TITLE = 'title',
  CREATED_AT = 'createdAtTimestamp',
  QUANTITY_FLOAT = 'quantityFloat',
  SUPPLIER = 'supplier',
  VALUE_FLOAT = 'valueFloat',
}

export const usePurchaseOrderItemResultDataGrid: UseDataGridConfig<SvbrgSearchPurchaseOrderItem> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.intake, {
    keyPrefix: 'PurchaseOrderItemResultDataGrid.column',
  });

  return useMemo(() => {
    const createColumn = gridColDefFactory({
      tColumn,
      t,
      defaultOptions: { sortable: false, align: GridAlignmentType.LEFT, minWidth: 120 },
    });
    return {
      sortModel: [],
      columnVisibilityModel: {},
      columns: [
        createColumn({
          field: PurchaseOrderItemSearchDataGridField.ID,
          renderCell: cellFactory(LinkCell, { needsPermission: Permission.BaseOrderList }),
          valueGetter: (_value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.orderDetail(row.parent.id),
            label: row.parent.sourceReference,
          }),
          hideable: false,
        }),
        createColumn({
          field: PurchaseOrderItemSearchDataGridField.TITLE,
          minWidth: 400,
        }),
        createColumn({
          field: PurchaseOrderItemSearchDataGridField.CREATED_AT,
          valueGetter: (_value, row) => new Date(row.createdAtTimestamp * 1000),
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
        }),
        createColumn({
          field: PurchaseOrderItemSearchDataGridField.QUANTITY_FLOAT,
        }),
        createColumn({
          field: PurchaseOrderItemSearchDataGridField.SUPPLIER,
          minWidth: 250,
          renderCell: cellFactory(CuriosityLinkCell, ({ row }) => ({
            ns: CuriosityNamespaceType.SUPPLIER,
            to: RouteId.supplierDetail(row.parent.supplierId),
            needs: { permission: Permission.BaseSupplierList },
          })),
          valueGetter: (_value, row) => row.parent.supplierId,
        }),
        createColumn({
          field: PurchaseOrderItemSearchDataGridField.VALUE_FLOAT,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({ currency: row.currency, value: row.valueFloat })),
        }),
      ],
    } as DataGridConfig<SvbrgSearchPurchaseOrderItem>;
  }, [tColumn, t]);
};
